import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BOARDS, GRADES } from "@/lib/nep";
import Navbar from "@/components/Navbar";
import Captcha from "@/components/Captcha";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { user, googleLogin, completeProfile } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ phone: "", grade: "", education_board: "", school_name: "" });
  const [err, setErr] = useState("");
  const [googleBusy, setGoogleBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Profile completion is only required right before the assessment — everyone
    // else who's already signed in belongs on their dashboard.
    if (user.has_paid && !(user.grade && user.education_board && user.school_name)) return;
    nav(user.has_paid ? "/assessment" : "/dashboard");
  }, [user, nav]);

  useEffect(() => {
    if (user?.phone) setForm((f) => (f.phone ? f : { ...f, phone: user.phone }));
  }, [user]);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) localStorage.setItem("pf_ref_code", ref.trim().toUpperCase());
  }, [searchParams]);

  const handleGoogleCredential = async (credential) => {
    setErr("");
    setGoogleBusy(true);
    try {
      await googleLogin(credential);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGoogleBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.phone.trim().length < 10) {
      setErr("Please enter a valid 10-digit phone number.");
      return;
    }
    if (!captchaOk) {
      setErr("Please solve the captcha correctly.");
      return;
    }
    setLoading(true);
    try {
      await completeProfile({
        phone: form.phone.trim(),
        grade: form.grade || undefined,
        education_board: form.education_board || undefined,
        school_name: form.school_name || undefined,
      });
      toast.success(`Welcome, ${user.name}!`);
      nav(user.has_paid ? "/assessment" : "/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setCaptchaOk(false);
      setCaptchaKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="card-brutal p-8">
          <span className="label-mono">Get started</span>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter mt-2">Create your account</h1>
          <p className="text-sm text-[#52525B] mt-2">Aligned with India's NEP 2020 — take the assessment & get AI career guidance.</p>

          {!user && (
            <div className="mt-8">
              <p className="label-mono mb-3">Step 1 · Sign in with Google</p>
              <GoogleSignInButton onCredential={handleGoogleCredential} />
              {googleBusy && (
                <div className="mt-3 text-sm flex items-center gap-2">
                  <Loader2 className="animate-spin" size={16} /> Signing you in…
                </div>
              )}
              {err && <div data-testid="register-error" className="mt-4 text-sm text-red-700 bg-red-100 border-2 border-red-700 rounded-lg px-3 py-2">{err}</div>}
            </div>
          )}

          {user && (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <p className="label-mono">Step 2 · Complete your profile</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label-mono block mb-2">Full name</label>
                  <input disabled value={user.name} className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-[#F4F4F5] text-[#52525B]" />
                </div>
                <div>
                  <label className="label-mono block mb-2">Email</label>
                  <input disabled value={user.email} className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-[#F4F4F5] text-[#52525B]" />
                </div>
                <div>
                  <label className="label-mono block mb-2">Phone number</label>
                  <input
                    data-testid="register-phone"
                    type="tel"
                    required
                    maxLength={10}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                    placeholder="10-digit mobile number"
                    className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label-mono block mb-2">Class / Grade</label>
                  <select data-testid="register-grade" required value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select class</option>
                    {GRADES.map((g) => <option key={g} value={g}>Class {g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-2">Education Board</label>
                  <select data-testid="register-board" required value={form.education_board} onChange={(e) => setForm({ ...form, education_board: e.target.value })} className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select board</option>
                    {BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-2">School</label>
                  <input data-testid="register-school" required value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <Captcha key={captchaKey} onChangeValid={setCaptchaOk} />

              {err && <div data-testid="register-error" className="text-sm text-red-700 bg-red-100 border-2 border-red-700 rounded-lg px-3 py-2">{err}</div>}

              <button type="submit" data-testid="register-submit" disabled={loading || !captchaOk} className="btn-brutal bg-blue-600 text-white w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="animate-spin" size={16} />} {user.has_paid ? "Continue to assessment" : "Continue to dashboard"}
              </button>
            </form>
          )}

          <p className="mt-6 text-sm text-center">
            Already have an account? <Link to="/login" className="font-semibold underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
